import { useQueryClient } from '@tanstack/react-query';
import { t } from '@/i18n/i18n';
import { useCallback, useEffect, useRef, useState } from 'react';
import { authApi } from '@/api/endpoints/auth';
import { billingApi } from '@/api/endpoints/billing';
import { isApiError } from '@/api/errors';
import type { ApiUser, CheckoutConflict, PlanSlug } from '@/api/types';
import { track } from '@/analytics/tracker';
import { useAuth } from '@/auth/AuthProvider';
import { uuidv4 } from '@/lib/ids';
import { describeCheckoutConflict, type BillingSurface } from './billingState';
import { checkoutGateway, onCheckoutEvent } from './paddle';

export type CheckoutStatus = 'idle' | 'opening' | 'processing' | 'activated' | 'failed';

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60_000;

/** Тексты — из текущего SPA (SubscriptionModal), словарь paywall.*; геттеры читают текущий язык. */
export const CHECKOUT_TEXT = {
  get notReady() {
    return t('paywall.notReady');
  },
  get noUser() {
    return t('paywall.noUser');
  },
  get failedStart() {
    return t('paywall.failedStart');
  },
  get failedOpen() {
    return t('paywall.failedOpen');
  },
  get error() {
    return t('paywall.error');
  },
  get slowActivation() {
    return t('paywall.slowActivation');
  },
};

/**
 * Опрос /me после оплаты и серверных мутаций подписки: вебхук Paddle приходит
 * асинхронно, поэтому успех — это подтверждённое состояние, а не 200 на запрос.
 */
export async function pollMe(
  condition: (user: ApiUser) => boolean,
  { intervalMs = POLL_INTERVAL_MS, timeoutMs = POLL_TIMEOUT_MS } = {},
): Promise<{ ok: boolean; attempts: number; durationMs: number }> {
  const start = Date.now();
  let attempts = 0;
  while (Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, intervalMs));
    attempts += 1;
    try {
      const { user } = await authApi.me();
      if (condition(user)) return { ok: true, attempts, durationMs: Date.now() - start };
    } catch {
      // временный сбой — продолжаем
    }
  }
  return { ok: false, attempts, durationMs: Date.now() - start };
}

/**
 * Что видно в самом оверлее (B6): валюта, страна покупателя, сумма и способ
 * оплаты из данных события Paddle — атрибуция потерь между «оверлей открылся»
 * и оплатой. Поля читаются защитно: их состав зависит от шага чекаута.
 */
function paddleEventMeta(data: Record<string, unknown> & { data?: Record<string, unknown> }) {
  const payload = data.data ?? {};
  const totals = payload['totals'] as Record<string, unknown> | undefined;
  const customer = payload['customer'] as Record<string, unknown> | undefined;
  const address = customer?.['address'] as Record<string, unknown> | undefined;
  const payment = payload['payment'] as Record<string, unknown> | undefined;
  const method = payment?.['method_details'] as Record<string, unknown> | undefined;
  return {
    currency: payload['currency_code'] ?? null,
    country: address?.['country_code'] ?? null,
    total: totals?.['total'] ?? null,
    payment_method: method?.['type'] ?? null,
  };
}

/** Каталог и деталка кэшированы под free-tier — после смены доступа перечитываем. */
export function useInvalidateCatalog() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['videos'] });
    void queryClient.invalidateQueries({ queryKey: ['video'] });
  }, [queryClient]);
}

/**
 * Одна попытка оплаты (BILL-5): correlation_id на попытку, POST /subscriptions/checkout
 * (409 = подписка уже есть → не ошибка, текст по `hint`), Paddle overlay, по checkout.completed —
 * опрос /me раз в 2 с до 60 с, при успехе обновление пользователя и кэша каталога.
 * `surface` — где показывается текст конфликта: на странице биллинга кнопки Resume и
 * «Сменить тариф» стоят под ним, из окна оплаты отправляем на страницу биллинга.
 */
export function useCheckout(options: { onActivated?: () => void; surface?: BillingSurface } = {}) {
  const { user, fetchUser } = useAuth();
  const surface = options.surface ?? 'modal';
  const invalidateCatalog = useInvalidateCatalog();
  const [status, setStatus] = useState<CheckoutStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const statusRef = useRef(status);
  statusRef.current = status;
  const correlationRef = useRef<string | null>(null);
  const onActivatedRef = useRef(options.onActivated);
  onActivatedRef.current = options.onActivated;

  const pollUntilActivated = useCallback(async () => {
    const correlation_id = correlationRef.current ?? undefined;
    track('subscription_polling_start', { correlation_id });
    const result = await pollMe((u) => !!u.has_subscription);
    if (result.ok) {
      await fetchUser();
      invalidateCatalog();
      setStatus('activated');
      track('subscription_polling_success', {
        correlation_id,
        metadata: { attempts: result.attempts, duration_ms: result.durationMs },
      });
      onActivatedRef.current?.();
      return;
    }
    track('subscription_polling_timeout', {
      correlation_id,
      metadata: { attempts: result.attempts, duration_ms: result.durationMs },
    });
    setStatus('failed');
    setError(CHECKOUT_TEXT.slowActivation);
  }, [fetchUser, invalidateCatalog]);

  useEffect(
    () =>
      onCheckoutEvent((ev) => {
        const correlation_id = correlationRef.current ?? undefined;
        const data = (ev.data ?? {}) as Record<string, unknown> & {
          data?: Record<string, unknown>;
        };
        switch (ev.name) {
          case 'checkout.loaded':
            track('paddle_checkout_loaded', { correlation_id, metadata: paddleEventMeta(data) });
            break;
          case 'checkout.warning':
            track('paddle_checkout_warning', {
              correlation_id,
              metadata: { warning_type: data['type'], message: data['message'] },
            });
            break;
          case 'checkout.completed':
            track('paddle_checkout_completed', {
              correlation_id,
              metadata: {
                transaction_id: data.data?.['transaction_id'] ?? data['transaction_id'],
                ...paddleEventMeta(data),
              },
            });
            setStatus('processing');
            void pollUntilActivated();
            break;
          case 'checkout.closed':
            track('paddle_checkout_closed', {
              correlation_id,
              metadata: { status_at_close: statusRef.current, ...paddleEventMeta(data) },
            });
            if (statusRef.current === 'opening') setStatus('idle');
            break;
          case 'checkout.payment.selected':
            track('paddle_payment_selected', {
              correlation_id,
              metadata: { payment_method: paddleEventMeta(data).payment_method },
            });
            break;
          case 'checkout.payment.initiated':
            track('paddle_payment_initiated', { correlation_id, metadata: paddleEventMeta(data) });
            break;
          case 'checkout.payment.failed':
            track('paddle_payment_failed', {
              correlation_id,
              metadata: { kind: 'declined', ...paddleEventMeta(data) },
            });
            break;
          case 'checkout.payment.error':
            // Сбой попытки оплаты: оверлей остаётся и даёт повторить — статус
            // чекаута не трогаем, только телеметрия.
            track('paddle_payment_failed', {
              correlation_id,
              metadata: {
                kind: 'error',
                error_type: data['type'],
                error_code: data['code'],
                message: data['message'] ?? data['detail'],
                ...paddleEventMeta(data),
              },
            });
            break;
          case 'checkout.error':
            track('paddle_checkout_error', {
              correlation_id,
              metadata: {
                error_type: data['type'],
                error_code: data['code'],
                message: data['message'] ?? data['detail'],
              },
            });
            setStatus('failed');
            setError(CHECKOUT_TEXT.error);
            break;
          default:
            break;
        }
      }),
    [pollUntilActivated],
  );

  const start = useCallback(
    async (plan: PlanSlug) => {
      const correlation_id = uuidv4();
      correlationRef.current = correlation_id;
      if (!user) {
        track('subscribe_blocked_no_user', { correlation_id, metadata: { plan } });
        setError(CHECKOUT_TEXT.noUser);
        return;
      }
      setStatus('opening');
      setError(null);

      try {
        await checkoutGateway.ensureReady();
      } catch (e) {
        track('subscribe_blocked_no_paddle', {
          correlation_id,
          metadata: { plan, paddle_error: (e as Error)?.message ?? null },
        });
        setStatus('idle');
        setError(CHECKOUT_TEXT.notReady);
        return;
      }

      const startedAt = Date.now();
      track('checkout_params_request', { correlation_id, metadata: { plan } });
      let params;
      try {
        params = await billingApi.checkout(plan, correlation_id);
      } catch (e) {
        if (isApiError(e) && (e.status === 409 || e.code === 'subscription_exists')) {
          const body = e.data as Partial<CheckoutConflict> | undefined;
          track('checkout_params_failed_conflict', {
            correlation_id,
            metadata: {
              plan,
              hint: body?.hint,
              existing_plan_slug: body?.current?.plan_slug,
              duration_ms: Date.now() - startedAt,
            },
          });
          setStatus('idle');
          setError(describeCheckoutConflict(body?.hint, surface));
          // Правда о подписке уже другая — перечитываем, чтобы страница показала Resume / Change plan.
          void fetchUser();
          return;
        }
        track('checkout_params_failed', {
          correlation_id,
          metadata: {
            plan,
            status: isApiError(e) ? e.status : undefined,
            error_message: (e as Error)?.message,
            duration_ms: Date.now() - startedAt,
          },
        });
        setStatus('failed');
        setError((e as Error)?.message || CHECKOUT_TEXT.failedStart);
        return;
      }
      track('checkout_params_success', {
        correlation_id,
        metadata: {
          plan,
          has_customer_id: !!params.customer_id,
          duration_ms: Date.now() - startedAt,
        },
      });

      try {
        checkoutGateway.open({ params, plan });
        track('paddle_checkout_open', {
          correlation_id,
          metadata: { plan, customer_id_present: !!params.customer_id },
        });
      } catch (e) {
        track('paddle_checkout_error', {
          correlation_id,
          metadata: {
            stage: 'sync_throw',
            error_message: (e as Error)?.message,
            error_name: (e as Error)?.name,
          },
        });
        setStatus('failed');
        setError((e as Error)?.message || CHECKOUT_TEXT.failedOpen);
      }
    },
    [user, fetchUser, surface],
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setError(null);
    correlationRef.current = null;
  }, []);

  return {
    status,
    error,
    start,
    reset,
    correlationRef,
    isBusy: status === 'opening' || status === 'processing',
  };
}
